// Logger utility to control logging based on environment
// REACT_APP_ENV can be: development, staging, production
// Logging enabled for: development (npm start) and staging (deployed)
// Logging disabled for: production (deployed)

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';
const appEnv = process.env.REACT_APP_ENV || 'development';

// Enable logging for development and staging
const loggingEnabled = appEnv === 'development' || appEnv === 'staging';

// Debug logging configuration on startup (ALWAYS log this to debug env var issues)
console.log('[Logger Config]', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_ENV: process.env.REACT_APP_ENV,
  appEnv,
  loggingEnabled,
  isDevelopment,
  isProduction
});

// Get app version from index
let APP_VERSION = '1.0.0';
try {
  // This will be available after index.tsx exports it
  const indexModule = require('../index');
  if (indexModule.APP_VERSION) {
    APP_VERSION = indexModule.APP_VERSION;
  }
} catch (e) {
  // Ignore if not available
}

class Logger {
  private appName = 'batchfather';
  private version = APP_VERSION;

  constructor() {
    // Always log app info on startup (in all environments)
    console.log(`${this.appName} v${this.version}`);
  }

  // Log general messages (when logging is enabled)
  log(...args: any[]) {
    if (loggingEnabled) {
      console.log(...args);
    }
  }

  // Log info messages (when logging is enabled)
  info(...args: any[]) {
    if (loggingEnabled) {
      console.info(...args);
    }
  }

  // Log warnings (when logging is enabled)
  warn(...args: any[]) {
    if (loggingEnabled) {
      console.warn(...args);
    }
  }

  // Log errors (always, even in production)
  error(...args: any[]) {
    console.error(...args);
  }

  // Log debug messages (when logging is enabled)
  debug(...args: any[]) {
    if (loggingEnabled) {
      console.debug(...args);
    }
  }

  // Log table data (when logging is enabled)
  table(data: any) {
    if (loggingEnabled && console.table) {
      console.table(data);
    }
  }

  // Start a timer (when logging is enabled)
  time(label: string) {
    if (loggingEnabled) {
      console.time(label);
    }
  }

  // End a timer (when logging is enabled)
  timeEnd(label: string) {
    if (loggingEnabled) {
      console.timeEnd(label);
    }
  }

  // Get app version
  getVersion() {
    return this.version;
  }

  // Get app name
  getAppName() {
    return this.appName;
  }

  // Check if development mode
  isDevelopment() {
    return isDevelopment;
  }

  // Check if production mode
  isProduction() {
    return isProduction;
  }
}

// Export singleton instance
export const logger = new Logger();

// Export default for convenience
export default logger;

